import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from '@/hooks/useMultiTenant';
import { useAuth } from '@/contexts/AuthContext';
import { useDeliveryMutations } from '@/hooks/useDeliveryMutations';
import { DeliveryWithDetails } from '@/types/delivery';
import { Package, MapPin, Search, Plus } from 'lucide-react';
import { toast } from 'sonner';

interface AddDeliveryToRouteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentDeliveryIds: string[];
  onSuccess?: () => void;
}

export const AddDeliveryToRouteDialog = ({
  open,
  onOpenChange,
  currentDeliveryIds,
  onSuccess,
}: AddDeliveryToRouteDialogProps) => {
  const { companyId } = useMultiTenant();
  const { user } = useAuth();
  const { assignDelivery } = useDeliveryMutations();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // Buscar APENAS entregas pendentes sem motorista atribuído
  const { data: availableDeliveries, isLoading } = useQuery({
    queryKey: ['available-deliveries', companyId, currentDeliveryIds],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deliveries')
        .select(`
          *,
          sale:sales(
            sale_number,
            customer:customers(name),
            total_value
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'pending')
        .is('assigned_to', null)
        .not('id', 'in', `(${currentDeliveryIds.length > 0 ? currentDeliveryIds.join(',') : 'null'})`)
        .order('delivery_date', { ascending: true })
        .order('priority', { ascending: false });

      if (error) throw error;
      
      return (data || []).map(d => ({
        ...d,
        items: [],
      })) as DeliveryWithDetails[];
    },
    enabled: open && !!companyId,
  });

  const filteredDeliveries = availableDeliveries?.filter((d) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      d.delivery_number?.toLowerCase().includes(search) ||
      d.delivery_address?.toLowerCase().includes(search) ||
      d.contact_name?.toLowerCase().includes(search) ||
      d.sale?.customer?.name?.toLowerCase().includes(search)
    );
  });

  const handleToggle = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (filteredDeliveries) {
      if (selectedIds.length === filteredDeliveries.length) {
        setSelectedIds([]);
      } else {
        setSelectedIds(filteredDeliveries.map((d) => d.id));
      }
    }
  };

  const handleAdd = async () => {
    if (selectedIds.length === 0 || !user?.id) return;

    setIsAdding(true);
    let successCount = 0;
    let skippedCount = 0;

    try {
      for (const deliveryId of selectedIds) {
        try {
          await assignDelivery({
            deliveryId,
            technicianUserId: user.id,
            instructions: 'Entrega adicionada à rota pelo motorista',
            sendNotification: false,
          });
          successCount++;
        } catch (err: any) {
          // Race condition: outro motorista já pegou esta entrega
          console.warn(`Entrega ${deliveryId} não disponível:`, err);
          skippedCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} entrega(s) adicionada(s) à rota`);
      }
      if (skippedCount > 0) {
        toast.warning(`${skippedCount} entrega(s) já foram assumidas por outro motorista`);
      }

      setSelectedIds([]);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error('Erro ao adicionar entregas:', error);
      toast.error('Erro ao adicionar entregas');
    } finally {
      setIsAdding(false);
    }
  };

  const handleClose = () => {
    if (isAdding) return;
    setSelectedIds([]);
    setSearchTerm('');
    onOpenChange(false);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500 text-white';
      case 'high':
        return 'bg-orange-500 text-white';
      case 'medium':
        return 'bg-yellow-500 text-white';
      default:
        return 'bg-gray-500 text-white';
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-2xl max-h-[90vh]"
        onEscapeKeyDown={(e) => { if (isAdding) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isAdding) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Adicionar Entregas à Rota
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por número, endereço ou cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-11"
            />
          </div>

          {/* Select All */}
          {filteredDeliveries && filteredDeliveries.length > 0 && (
            <div className="flex items-center justify-between">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                className="text-sm"
              >
                {selectedIds.length === filteredDeliveries.length
                  ? 'Desmarcar todos'
                  : 'Selecionar todos'}
              </Button>
              <span className="text-sm text-muted-foreground">
                {selectedIds.length} de {filteredDeliveries.length} selecionada(s)
              </span>
            </div>
          )}

          {/* List */}
          <ScrollArea className="h-[400px] pr-4">
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse p-4 border rounded-lg">
                    <div className="h-4 bg-muted rounded w-1/3 mb-2" />
                    <div className="h-3 bg-muted rounded w-2/3" />
                  </div>
                ))}
              </div>
            ) : filteredDeliveries?.length === 0 ? (
              <div className="text-center py-8">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-muted-foreground">
                  Nenhuma entrega disponível
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredDeliveries?.map((delivery) => (
                  <div
                    key={delivery.id}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedIds.includes(delivery.id)
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/50'
                    }`}
                    onClick={() => handleToggle(delivery.id)}
                  >
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={selectedIds.includes(delivery.id)}
                        onCheckedChange={() => handleToggle(delivery.id)}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <p className="font-medium">
                              {delivery.sale?.customer?.name ||
                                delivery.contact_name ||
                                'Cliente'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {delivery.delivery_number}
                            </p>
                          </div>
                          <Badge className={getPriorityColor(delivery.priority)}>
                            {delivery.priority === 'urgent'
                              ? 'Urgente'
                              : delivery.priority === 'high'
                              ? 'Alta'
                              : delivery.priority === 'medium'
                              ? 'Média'
                              : 'Baixa'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">{delivery.delivery_address}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleClose} disabled={isAdding}>
            Cancelar
          </Button>
          <Button
            onClick={handleAdd}
            disabled={selectedIds.length === 0 || isAdding}
            className="min-w-[150px]"
          >
            {isAdding
              ? 'Adicionando...'
              : `Adicionar ${selectedIds.length > 0 ? `(${selectedIds.length})` : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
